import { useEffect, useMemo, useRef } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import dayjs from 'dayjs';
import { useInView } from 'react-intersection-observer';
import { createJSONStorage } from 'zustand/middleware';
import { useCallIntelDemoShallowStore } from '@frontend/api-call-intel';
import { UsersQueries } from '@frontend/api-users';
import { checkAggregateACL, getUser } from '@frontend/auth-helpers';
import { useAppFlagStore } from '@frontend/shared';
import { createStoreWithPersistAndSubscribe } from '@frontend/store';
import { pendo } from '@frontend/tracking';
import { useComponentEngagementStore } from '../../common/store/component-engagement-store';
import {
  DATA_ANALYST_CAMPAIGN_END_FF,
  DATA_ANALYST_PROMO_MAX_DISPLAY_DAYS,
  DATA_ANALYST_ROLE_NAME,
  DATA_ANALYST_ROLE_PROMO_FF,
} from '../constants';
import { getDataAnalystPromoTrackingId } from '../tracking-ids';
import { DataAnalystRolePromoSource } from '../types';

interface DataAnalystRolePromoStore {
  firstSeenSourceByUser: Record<string, DataAnalystRolePromoSource>;
  setFirstSeenSourceByUser: (source: DataAnalystRolePromoSource, userId: string) => void;
}

const useDataAnalystRolePromoStore = createStoreWithPersistAndSubscribe<DataAnalystRolePromoStore>(
  (set, get) => ({
    firstSeenSourceByUser: {},
    setFirstSeenSourceByUser: (source: DataAnalystRolePromoSource, userId: string) =>
      set(() => ({ firstSeenSourceByUser: { ...get().firstSeenSourceByUser, [userId]: source } })),
  }),
  {
    name: 'call-intel:data-analyst-promo',
    storage: createJSONStorage(() => localStorage),
  },
  { name: 'call-intel:data-analyst-promo', trace: true }
);

const COMPONENT_ID_FOR_TRACK = 'call-intel:data-analyst-role-promo';

export const useDataAnalystRolePromo = ({
  source,
  locationIds,
}: {
  source: DataAnalystRolePromoSource;
  locationIds: string[];
}) => {
  const user = getUser();
  const userId = user?.username;

  const { isDemoAccount } = useCallIntelDemoShallowStore('isDemoAccount');
  const { getFeatureFlagValue } = useAppFlagStore();

  const isPromoFFEnabled = getFeatureFlagValue({ locationIds, flagName: DATA_ANALYST_ROLE_PROMO_FF });
  const isPromoCampaignEnded = getFeatureFlagValue({ locationIds, flagName: DATA_ANALYST_CAMPAIGN_END_FF });

  const targetedLocationIDsRef = useRef<string[]>([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  const { firstSeenSourceByUser, setFirstSeenSourceByUser } = useDataAnalystRolePromoStore();
  const { getEngagementData, trackInteraction, trackVisibility } = useComponentEngagementStore();

  // Track user wise component engagement
  const { firstSeen: firstSeenISODate, interactionCount } = getEngagementData({
    componentId: COMPONENT_ID_FOR_TRACK,
    source: userId,
  });

  const { aggregateValue: hasAdminAccess } = checkAggregateACL(locationIds, [Permission.USER_WRITE]);

  const shouldCheckForMissingDataAnalystRole = useMemo(() => {
    if (!userId || !locationIds?.length) return false;

    // Don't show promo in demo accounts
    if (isDemoAccount) return false;

    // Only admins should see the promo
    if (!hasAdminAccess) return false;

    // Should enable promo if FF is enabled
    if (!isPromoFFEnabled) return false;

    // First seen promo source should match the current source
    if (firstSeenSourceByUser[userId] && firstSeenSourceByUser[userId] !== source) return false;

    // Check if action has already been taken
    if (interactionCount > 0) return false;

    // Check if the promo exceeded max display days
    if (firstSeenISODate && dayjs().diff(dayjs(firstSeenISODate), 'days') >= DATA_ANALYST_PROMO_MAX_DISPLAY_DAYS) {
      return false;
    }

    // If campaign ended and user has not seen the promo, hide it
    if (isPromoCampaignEnded && !firstSeenSourceByUser[userId]) return false;

    return true;
  }, [
    userId,
    locationIds?.length,
    hasAdminAccess,
    isDemoAccount,
    isPromoFFEnabled,
    firstSeenSourceByUser,
    source,
    interactionCount,
    firstSeenISODate,
    isPromoCampaignEnded,
  ]);

  // Fetch Data Analyst role ID as it can change across environments
  const { data: dataAnalystRoleId } = UsersQueries.useGetUserPermissions({
    retry: false,
    enabled: shouldCheckForMissingDataAnalystRole,
    select: (response) => response.roles?.find((role) => role.name === DATA_ANALYST_ROLE_NAME)?.roleId,
  });

  const { data: isDataAnalystRoleMissing, isLoading: isRoleCheckInProgress } = UsersQueries.useGetUserRoleAnalytics(
    {
      locationIds,
      roleIds: dataAnalystRoleId ? [dataAnalystRoleId] : [],
    },
    {
      enabled: !!dataAnalystRoleId && shouldCheckForMissingDataAnalystRole,
      retry: false,
      select: (response) => {
        // Filters locationIds to only those without any Data Analyst users
        targetedLocationIDsRef.current = locationIds.filter((locationId) => {
          const matchingRoleMap = response.locationRoles?.find((roleMap) => roleMap.locationId === locationId);

          // Include if no data for this location
          if (!matchingRoleMap) return true;

          // Check if any user has the Data Analyst role
          const hasDataAnalystUsers = matchingRoleMap.roleUserCounts?.some(
            ({ roleId, userCount }) => roleId === dataAnalystRoleId && !!userCount
          );

          // Include if there are no Data Analyst users
          return !hasDataAnalystUsers;
        });

        return targetedLocationIDsRef.current.length > 0;
      },
    }
  );

  const shouldShowPromo = shouldCheckForMissingDataAnalystRole && !isRoleCheckInProgress && isDataAnalystRoleMissing;

  useEffect(() => {
    if (shouldShowPromo && inView && userId) {
      pendo.track(getDataAnalystPromoTrackingId({ source, action: 'banner-visible' }), {
        userId,
        targetedLocationIds: targetedLocationIDsRef.current,
        bannerId: COMPONENT_ID_FOR_TRACK,
        source,
      });
      trackVisibility({ componentId: COMPONENT_ID_FOR_TRACK, source: userId });
      setFirstSeenSourceByUser(source, userId);
    }
  }, [inView, setFirstSeenSourceByUser, shouldShowPromo, source, trackVisibility, userId]);

  const handlePromoInteraction = () => {
    trackInteraction({ componentId: COMPONENT_ID_FOR_TRACK, source: userId ?? '' });
  };

  return {
    ref,
    shouldShowPromo,
    handlePromoInteraction,
  };
};
